import React, { useEffect, useRef, useCallback } from 'react';
import SignaturePad from 'signature_pad';

const SignatureCanvas = ({ file, onRemove, saveSignature, clearSignature }) => {
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const signaturePadRef = useRef(null);
    const isDrawingRef = useRef(false);

    const resizeCanvas = useCallback((canvas, img) => {
        const container = canvas.parentElement;
        const maxWidth = container.clientWidth;
        const maxHeight = container.clientHeight;

        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height = height * ratio;
        }

        if (height > maxHeight) {
            const ratio = maxHeight / height;
            height = maxHeight;
            width = width * ratio;
        }

        canvas.width = width;
        canvas.height = height;

        return { width, height };
    }, []);

    const handleClearSignature = useCallback(() => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            clearSignature(file.id);
        }
    }, [file.id, clearSignature]);

    const handlePointerDown = useCallback(() => {
        isDrawingRef.current = true;
    }, []);

    const handlePointerUp = useCallback(() => {
        if (isDrawingRef.current && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
            const signatureData = signaturePadRef.current.toDataURL();
            console.log('Captured signature:', signatureData);
            saveSignature(file.id, signatureData);
        }
        isDrawingRef.current = false;
    }, [file.id, saveSignature]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const signaturePad = new SignaturePad(canvas, {
            penColor: "rgb(255, 0, 0)",
            backgroundColor: "rgba(255, 255, 255, 0)"
        });
        signaturePadRef.current = signaturePad;

        // Add event listeners
        window.addEventListener('mousedown', handlePointerDown);
        window.addEventListener('mouseup', handlePointerUp);

        const img = new Image();
        imgRef.current = img;

        img.onload = () => {
            const { width, height } = resizeCanvas(canvas, img);
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            if (file.signatureData) {
                signaturePad.fromDataURL(file.signatureData);
            }
        };

        img.src = file.src;

        const handleResize = () => {
            if (imgRef.current?.complete) {
                const { width, height } = resizeCanvas(canvas, imgRef.current);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(imgRef.current, 0, 0, width, height);

                if (!signaturePad.isEmpty()) {
                    signaturePad.fromDataURL(signaturePad.toDataURL());
                }
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener('mousedown', handlePointerDown);
            window.removeEventListener('mouseup', handlePointerUp);

            signaturePad.off();
        };
    }, [file, resizeCanvas, handlePointerDown, handlePointerUp]);

    return (
        <div className="fileItem" style={{ marginBottom: '20px' }}>
            <div className="signatureBoxs">
                <canvas ref={canvasRef} />
            </div>
            <div className="signature-controls" style={{ marginTop: '10px' }}>
                <button
                    type="button"
                    onClick={handleClearSignature}
                    className="btn btn-secondary"
                    style={{ marginRight: '10px' }}
                >
                    Clear
                </button>
                <button
                    type="button"
                    onClick={() => onRemove(file.id)}
                    className="btn btn-danger"
                >
                    Remove
                </button>
            </div>
        </div>
    );
};

export default React.memo(SignatureCanvas);